/**
 * Smooth Progress Hook
 *
 * Provides smooth animation for progress values using requestAnimationFrame.
 * Gradually transitions from current displayed progress to target progress.
 *
 * Features:
 * - Smooth easing animation
 * - Configurable speed
 * - Automatic cleanup
 * - Snaps to target when close enough
 *
 * @module hooks/useSmoothProgress
 */

import { useState, useEffect, useRef } from 'react';

// ============================================================================
// ANIMATION CONSTANTS
// ============================================================================

/** When difference is less than this threshold, snap directly to target */
const SNAP_THRESHOLD = 0.5;

/** Base easing factor for exponential animation (multiplied by speed) */
const EASING_FACTOR = 0.08;

/** Minimum step size to ensure animation completes in reasonable time */
const MIN_STEP = 0.5;

/**
 * Options for smooth progress animation
 */
export interface UseSmoothProgressOptions {
  /**
   * Animation speed multiplier
   * Higher values = faster animation
   * @default 2
   */
  speed?: number;
}

/**
 * Hook for smooth progress animation
 *
 * Animates from current displayed value toward target value using
 * requestAnimationFrame for smooth 60fps transitions.
 *
 * @param targetProgress - The target progress value (0-100)
 * @param options - Animation configuration options
 * @returns The smoothly animated progress value (0-100)
 *
 * @example
 * ```tsx
 * function ProgressBar({ actualProgress }: { actualProgress: number }) {
 *   const displayProgress = useSmoothProgress(actualProgress);
 *
 *   return (
 *     <div>
 *       <p>{displayProgress}%</p>
 *       <Progress value={displayProgress} />
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With custom speed
 * const displayProgress = useSmoothProgress(actualProgress, { speed: 3 });
 * ```
 */
export function useSmoothProgress(
  targetProgress: number,
  options: UseSmoothProgressOptions = {}
): number {
  const { speed = 2 } = options;

  // State for the displayed progress value - triggers re-renders when updated
  const [displayProgress, setDisplayProgress] = useState(targetProgress);

  // ============================================================================
  // WHY REFS INSTEAD OF STATE FOR ANIMATION VALUES?
  // ============================================================================
  // We use refs for animation tracking because:
  // 1. State updates are asynchronous and batched - we need immediate access to
  //    current values within the animation frame callback
  // 2. State changes trigger re-renders - the animation loop runs at 60fps and
  //    we don't want 60 re-renders per second for intermediate values
  // 3. Refs persist across renders without causing re-renders themselves
  // 4. The animation loop reads/writes these values synchronously each frame
  //
  // Only when we have a meaningful visual change do we update displayProgress
  // state to trigger a single re-render with the new value.
  // ============================================================================

  /**
   * Stores the requestAnimationFrame ID for cleanup purposes.
   * Used to cancel the animation loop when the component unmounts.
   * Also serves as a flag to check if animation is already running.
   */
  const animationRef = useRef<number | null>(null);

  /**
   * Stores the target progress value we're animating toward.
   * Updated synchronously when targetProgress prop changes, allowing
   * the animation loop to immediately "see" the new target without
   * restarting the entire animation or effect.
   */
  const targetRef = useRef(targetProgress);

  /**
   * Tracks the current interpolated value during animation.
   * This is the "source of truth" for where we are in the animation,
   * separate from the React state which only updates for renders.
   * Allows smooth continuation when target changes mid-animation.
   */
  const currentValueRef = useRef(targetProgress);

  // Synchronously update targetRef whenever prop changes.
  // This happens during render, before the animation frame runs,
  // so the animation loop always has the latest target value.
  targetRef.current = targetProgress;

  useEffect(() => {
    // Flag to stop animation when effect cleans up
    let isAnimating = true;

    /**
     * Animation frame callback - runs approximately 60 times per second.
     * Implements exponential easing (ease-out) by moving a fraction of
     * the remaining distance each frame.
     */
    const animate = () => {
      // Early exit if component unmounted or effect cleaned up
      if (!isAnimating) return;

      // Read current values from refs (not state)
      const target = targetRef.current;
      const current = currentValueRef.current;
      const diff = target - current;

      // ========================================================================
      // SNAP-TO-TARGET THRESHOLD
      // ========================================================================
      // When difference is less than SNAP_THRESHOLD, snap directly to target
      // to avoid infinite asymptotic approach. Without this, exponential
      // easing would never actually reach the target (e.g., 99.9 -> 99.95...).
      // ========================================================================
      if (Math.abs(diff) < SNAP_THRESHOLD) {
        if (current !== target) {
          currentValueRef.current = target;
          setDisplayProgress(target);
        }
        // Continue running animation loop even when at target - this allows
        // immediate response when targetProgress prop changes without
        // restarting the effect. The loop stays "warm" and responsive.
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      // ========================================================================
      // EASING/INTERPOLATION LOGIC
      // ========================================================================
      // Exponential easing (ease-out): Move a percentage of remaining distance.
      // Formula: step = diff * EASING_FACTOR * speed
      //
      // How it works:
      // - When far from target (large diff), steps are large -> fast movement
      // - When close to target (small diff), steps are small -> smooth deceleration
      // - This creates natural "ease-out" feel without complex math
      //
      // Example at speed=2, target=100, current=0:
      //   Frame 1: diff=100, step=16, current=16
      //   Frame 2: diff=84, step=13.4, current=29.4
      //   Frame 3: diff=70.6, step=11.3, current=40.7
      //   ... gradually slowing as it approaches 100
      // ========================================================================
      const step = diff * EASING_FACTOR * speed;

      // Minimum step ensures animation completes in reasonable time.
      // Without this, very small differences would result in imperceptibly
      // small steps (e.g., diff=1 -> step=0.16 -> 6+ frames for 1% change).
      // The sign (+/-) preserves direction toward target.
      const minStep = diff > 0 ? MIN_STEP : -MIN_STEP;

      // Use whichever step is larger in magnitude to ensure both:
      // - Smooth deceleration when far from target (exponential step wins)
      // - Guaranteed progress when close to target (minimum step wins)
      const actualStep = Math.abs(step) > Math.abs(minStep) ? step : minStep;

      // Update the current value ref for next frame's calculation
      currentValueRef.current = current + actualStep;

      // Trigger re-render with new display value
      setDisplayProgress(currentValueRef.current);

      // Schedule next animation frame - creates the continuous loop
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation loop only once on mount (or when speed changes).
    // The null check prevents starting multiple concurrent loops.
    if (!animationRef.current) {
      animationRef.current = requestAnimationFrame(animate);
    }

    // Cleanup function runs on unmount or when speed changes
    return () => {
      // Set flag to stop any pending animation callback
      isAnimating = false;
      // Cancel any scheduled animation frame to prevent memory leaks
      // and updates to unmounted component
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [speed]); // Only re-run effect if speed changes, NOT targetProgress
  // (targetProgress changes are handled via ref update above)

  // Round to whole number for display (avoids flickering decimal places)
  return Math.round(displayProgress);
}
